/**
 * The license fields the product lines (Traverse, Process Pro) are asked for, by the type of the environment chosen. The one place
 * to change them; the backend mirrors them in apps/infrastructure/config/catalog.py and checks the right set for the environment.
 *
 *   production         Site ID, and a License key in two parts: Production key and Sandbox key
 *   staging, demo      License key and Site ID
 *
 * Every field is required. No format is checked beyond that: none was given. [CONFIRM a format, if there is one]
 * The license is not a secret and is not used to build infrastructure: it is kept with the request, as entered, for the application
 * deployment that follows. It is shown as typed, and in full on the Review step.
 */
import type { EnvironmentType } from '@/data/environmentTypes'

export type LicenseFieldKey = 'siteId' | 'licenseKey' | 'productionKey' | 'sandboxKey'

export interface LicenseField {
  key: LicenseFieldKey
  label: string
  /** One of the two parts of the production License key (Production key, Sandbox key): grouped under PRODUCTION_LICENSE_KEY_HEADING. */
  keyPart: boolean
}

const SITE_ID: LicenseField = { key: 'siteId', label: 'Site ID', keyPart: false }
const LICENSE_KEY: LicenseField = { key: 'licenseKey', label: 'License key', keyPart: false }

export const LICENSE_FIELDS: Readonly<Record<EnvironmentType, readonly LicenseField[]>> = Object.freeze({
  production: [
    SITE_ID,
    { key: 'productionKey', label: 'Production key', keyPart: true },
    { key: 'sandboxKey', label: 'Sandbox key', keyPart: true },
  ],
  staging: [LICENSE_KEY, SITE_ID],
  demo: [LICENSE_KEY, SITE_ID],
})

/** The two parts of the production License key are grouped under this heading. */
export const PRODUCTION_LICENSE_KEY_HEADING = 'License key'

export const licenseFieldsFor = (type: EnvironmentType | null): readonly LicenseField[] => (type ? LICENSE_FIELDS[type] : [])
